"use client";

import { Session } from "next-auth";
import { getSession } from "next-auth/react";
import { createContext, useContext, useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import Cookies from "js-cookie";
import { DEVICE_ID_COOKIE_NAME } from "@/models/DeviceId";

interface LazySessionContext {
  session: Session | null;
  isAuthenticated: boolean;
  isLoaded: boolean;
  refreshSession: () => Promise<void>;
  deviceId: string;
}

const lazySessionContext = createContext<LazySessionContext | undefined>({
  session: null,
  isAuthenticated: false,
  isLoaded: false,
  refreshSession: async () => {
    throw new Error("refreshSession not implemented");
  },
  deviceId: "",
});

function GetDeviceId(): string {
  let deviceId = Cookies.get(DEVICE_ID_COOKIE_NAME);

  if (!deviceId) {
    const newDeviceId = uuidv4();
    Cookies.set(DEVICE_ID_COOKIE_NAME, newDeviceId);
    deviceId = newDeviceId;
  }

  return deviceId ?? "";
}

export function LazySessionProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [sessionState, setSessionState] = useState<{
    session: Session | null;
    isLoaded: boolean;
    deviceId: string;
  }>({ session: null, isLoaded: false, deviceId: "" });

  const refreshSession = async () => {
    const session = await getSession();
    setSessionState({
      session,
      isLoaded: true,
      deviceId: GetDeviceId(),
    });
  };

  useEffect(() => {
    if (!sessionState.isLoaded) {
      refreshSession();
    }
  }, [sessionState]);

  const contextValue: LazySessionContext = {
    ...sessionState,
    isAuthenticated: sessionState?.session?.user?.id !== undefined,
    refreshSession,
  };

  return (
    <lazySessionContext.Provider value={contextValue}>
      {children}{" "}
    </lazySessionContext.Provider>
  );
}

export function useLazySession() {
  const context = useContext(lazySessionContext);
  if (!context) {
    throw new Error("useLazySession must be used within a LazySessionProvider");
  }
  return context;
}
